import { Injectable, NgZone, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { Content } from '@23degrees/shared-code/models';
import { cloneObject } from '@23degrees/shared-code/utilities';
import { MessageBus } from '@23degrees/shared/bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';
import { DataService } from '@23degrees/shared/services/data/data.service';
import { GlobalService } from '@23degrees/shared/services/global/global.service';
import { REQUEST_MESSAGES, RESPONSE_MESSAGES } from '@config';

import {
  addMetaInfo,
  applyEditorFilter,
  applyInVisualFilter,
  applySmartFilter,
  applySmartFunctions,
  clearNulls,
  getValueRange,
  patchMomentMMM,
  processDataPerType,
  sortByContent,
  updateOptimalRoundingFormat,
} from '@23degrees/shared-code/data/pipeline';
import { Field } from '@23degrees/shared-code/models';
import { ColorManager } from '../color/color.manager';

/*
    A global Data Manager
    - holds and caches Raw Data (not Content)
    - uses DataService to get the data
    - holds it locally
    - uses messagebus to inform components that data is here
    - has functions to request data -> for components

    Data Stages:
    Raw Data -> complete dataset data
    SmartFunctionData -> in rawDAta out-> smartFunction data
    VisualData ->


*/

interface IMessage {
  [key: string]: any;
}

interface IContentMessage extends IMessage {
  content?: Content;
}

@Injectable({
  providedIn: 'root',
})
export class DataManager extends BaseClass {
  private messageBus = inject(MessageBus);
  private dataService = inject(DataService);
  private ngZone = inject(NgZone);
  private gobalService = inject(GlobalService);
  private colorManager = inject(ColorManager);

  // holds our data identifer id and then timestamp
  // TODO when to better empty cache?
  private rawData: any = {};

  // preRender Data holds cached data transformed before
  // inVisual Filters and sorting
  private preRenderData: any = {};

  public rawData$ = new BehaviorSubject(null);
  public renderData$ = new BehaviorSubject(null);

  constructor() {
    super();
    // console.log('[DataManager] Constructor');
    this.messageBus.process(REQUEST_MESSAGES.RawData, this.processDataById, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.SmartFunctionData, this.processSmartFunctionData, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.RenderData, this.processRenderData, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.DataUpdate, this.processDataUpdate, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.InjectData, this.processInjectData, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.FilterUpdate, this.processInVisualChange, this.componentDestroyed$);
    this.messageBus.process(REQUEST_MESSAGES.DataPurge, this.processDataPurge, this.componentDestroyed$);
  }

  /**
   * emptys complete cache (carefull should only be used on logout
   */
  public emptyCache = () => {
    this.preRenderData = {};
    this.rawData = {};
  };
  /**
   * purges a data id manually - carefull!
   */
  public purgeData = (dataId) => {
    if (!dataId) return;
    // console.log('[purgeData]', dataId);
    delete this.rawData[dataId];
  };

  public setDataById = (dataId, data) => {
    if (this.rawData[dataId]) {
      this.rawData[dataId].data = data;
    } else {
      this.rawData[dataId] = {
        _id: dataId,
        data: data,
      };
    }
  };

  private processDataPurge = (msg, recipients, referenceID) => {
    if (!msg.dataId) return;
    // console.log('[DataManager] - purging Data', msg.dataId);
    this.purgeData(msg.dataId);
    // console.log("[DataManager] - after Purge Data",this.rawData[msg.dataId]);
    this.messageBus.emit(RESPONSE_MESSAGES.DataPurge, recipients, msg.dataId, referenceID);
  };

  public processInjectData = (msg, recipients, referenceID) => {
    if (!msg._id) {
      // console.log('ERROR [DataManager] - no data id for process Injected data', msg, recipients);
      return;
    }

    try {
      sessionStorage.setItem('data', JSON.stringify(msg));
    } catch (e) {
      console.log('[DataManager] - No access to session storage');
    }

    this.rawData[msg._id] = msg;
  };

  private processDataById = (msg, recipients, referenceID) => {
    if (!msg.dataId) return;
    this.requestDataById(msg.dataId, recipients, msg.embed, referenceID);
  };

  public processDataUpdate = (msg, recipients, referenceID) => {
    if (!msg.dataId || !this.rawData[msg.dataId]) return;

    this.rawData[msg.dataId].data = msg.data;
    this.messageBus.emit(RESPONSE_MESSAGES.DataUpdate, recipients, this.rawData[msg.dataId], referenceID);
  };

  private processSmartFunctionData = async (msg: IContentMessage, recipients, referenceID) => {
    const { content, data, dataId, embed } = msg;
    const smart = await this.ngZone.runOutsideAngular(async () => {
      await this.fetchDataById(dataId, embed);
      const smart = applySmartFunctions(content, data || this.rawData[dataId].data);
      return smart;
    });

    this.ngZone.run(() => {
      this.messageBus.emit(
        RESPONSE_MESSAGES.SmartFunctionData,
        recipients,
        {
          data: smart.data,
          content: smart.content,
        },
        referenceID,
      );
    });
  };

  /**
   * Complete Pipeline
   * cause of performance of change
   * @param  msg        [description]
   * @param  recipients [description]
   * @return            [description]
   */
  private processRenderData = async (msg: IContentMessage, recipients, referenceID) => {
    let startDate: any = new Date();
    let endDate: any;
    const { content, data, dataId, embed, options } = msg;
    // console.log('[DataManager] - process renderData Start');
    const smart = await this.ngZone.runOutsideAngular(async () => {
      await this.fetchDataById(dataId, embed);

      patchMomentMMM(content, this.rawData[dataId].data);

      this.emitCachedRawData(dataId, recipients, referenceID);
      // we need to break object references for smart function
      // changes inside the data otherwise if we apply smart
      // functions we change the raw data reference which is
      // BAD!
      const inputData = cloneObject(data || this.rawData[dataId].data);
      const addAutoFields = !content.typeSpecific.inlineData && content.type !== 'dataset' && content.typeSpecific.fields;

      // WARNING EXPERIMENTAL
      if (addAutoFields) {
        // check if we have our autIndex Field if needed (only one label or miscfield or timefield there)
        if (!Object.keys(content.typeSpecific.fields).includes('auto23degreesseries')) {
          const field: Field = {
            localStorage: {
              isChanging: false,
            },
            dataType: 'string',
            fieldName: 'auto23degreesseries',
            fieldType: 'miscField',
            isoLabelField: null,
            isoIsoField: null,
            isoMappingId: null,
            autoGenerated: true,
            isNeededForUniqueness: false,
            selectedValues: [],
            title: 'Series',
            format: {
              formatType: 'string',
              locale: 'de-DE',
              number: {
                numberFormat: '.0f',
                decimalPlaces: 0,
                digitGrouping: true,
                shorten: false,
                showSign: false,
                showZero: true,
              },
              date: {
                locale: 'de-DE',
                inputFormat: 'YYYY',
                dateFormat: 'YYYY',
              },
              prefix: '',
              suffix: '',
            },
          };
          content.typeSpecific.fields[field.fieldName] = field;
        }

        if (!Object.keys(content.typeSpecific.fields).includes('auto23degreesindex')) {
          const field: Field = {
            localStorage: {
              isChanging: false,
            },
            dataType: 'string',
            fieldName: 'auto23degreesindex',
            fieldType: 'miscField',
            isoLabelField: null,
            isoIsoField: null,
            isoMappingId: null,
            autoGenerated: true,
            isNeededForUniqueness: false,
            selectedValues: [],
            title: 'Index',
            format: {
              formatType: 'string',
              locale: 'de-DE',
              number: {
                numberFormat: '.0f',
                decimalPlaces: 0,
                digitGrouping: true,
                shorten: false,
                showSign: false,
                showZero: true,
              },
              date: {
                locale: 'de-DE',
                inputFormat: 'YYYY',
                dateFormat: 'YYYY',
              },
              prefix: '',
              suffix: '',
            },
          };
          content.typeSpecific.fields[field.fieldName] = field;
        }

        let index = 1;
        for (const row of inputData) {
          if (!row['auto23degreesseries']) row['auto23degreesseries'] = 'Series';
          if (!row['auto23degreesindex']) row['auto23degreesindex'] = `Index ${String(index).padStart(9, '0')}`;
          index++;
        }
      }
      // WARNING END

      const meta = addMetaInfo(content, inputData);
      const smart = applySmartFunctions(meta.content, meta.data);

      return smart;
    });

    this.messageBus.emit(
      RESPONSE_MESSAGES.PreFilterData,
      recipients,
      {
        data: smart.data,
        content: smart.content,
      },
      referenceID,
    );

    const ready = this.ngZone.runOutsideAngular(() => {
      // filter
      const filter = applyEditorFilter(smart.content, smart.data);
      const smartFilterData = applySmartFilter(filter.data, filter.content);

      // console.log('set preRenderData', dataId, filter.content.slug, smartFilterData);
      this.preRenderData[`${dataId}~${filter.content.slug}`] = smartFilterData;
      const fullRange = getValueRange(filter.content, smartFilterData);

      // update colors
      if (filter.content?.typeSpecific?.style?.colors2) {
        this.colorManager.reevaluateColor(filter.content.typeSpecific.style.colors2, filter.data);
      }

      // @ts-ignore -> WARNING image service PATCHES content
      const visual = applyInVisualFilter(filter.content, smartFilterData, !content.noResetInvisual);

      // clear nulls
      const clear = clearNulls(visual.content, visual.data);

      // Type processing
      clear.data = processDataPerType(clear.content, clear.data);

      // update optimal rounding format
      updateOptimalRoundingFormat(clear.content, clear.data);

      const activeRange = getValueRange(clear.content, clear.data);

      // sort data
      const sorted = sortByContent(clear.content, clear.data);

      //typeSpecific.data.valueRange -> only the mode and if it is user Specific save the values there and on runtime write to localstorage
      let range: number[];
      const { valueRange } = content.typeSpecific.data;
      if (valueRange.type === 'full') range = fullRange;
      else if (['active', 'auto'].includes(valueRange.type)) range = activeRange;
      else {
        if (valueRange.range[0] === 0 && valueRange.range[1] === 0) {
          valueRange.range[0] = fullRange[0];
          valueRange.range[1] = fullRange[1];
        }

        range = valueRange.range;
      }

      return {
        range,
        sorted,
      };
    });

    this.ngZone.run(() => {
      this.messageBus.emit(
        RESPONSE_MESSAGES.RenderData,
        recipients,
        {
          data: ready.sorted.data,
          preRenderData: this.preRenderData[`${dataId}~${ready.sorted.content.slug}`],
          preFilterData: smart.data,
          content: ready.sorted.content,
          range: ready.range,
          options: options,
        },
        referenceID,
      );
      // manfred's hacks for listening to description
      this.renderData$.next({ _id: dataId, data: ready.sorted.data });
    });
  };

  private processInVisualChange = async (msg, recipients, referenceID) => {
    const { content, dataId, options } = msg;
    const data = this.preRenderData[`${dataId}~${content.slug}`];

    // console.log('[DataManager] processInVisualChange', dataId, content.slug, Object.keys(this.preRenderData));

    const fullRange = getValueRange(content, data);
    const visual = applyInVisualFilter(content, data, false);
    const activeRange = getValueRange(visual.content, visual.data);

    // clear nulls
    const clear = clearNulls(visual.content, visual.data);

    // update optimal rounding format
    updateOptimalRoundingFormat(clear.content, clear.data);

    // sort data
    const sorted = sortByContent(clear.content, clear.data);

    let range: number[];
    const { valueRange } = content.typeSpecific.data;
    if (valueRange.type === 'full') range = fullRange;
    else if (['active', 'auto'].includes(valueRange.type)) range = activeRange;
    else {
      if (valueRange.range[0] === 0 && valueRange.range[1] === 0) {
        valueRange.range[0] = fullRange[0];
        valueRange.range[1] = fullRange[1];
      }

      range = valueRange.range;
    }

    this.messageBus.emit(
      RESPONSE_MESSAGES.RenderData,
      recipients,
      {
        data: sorted.data,
        content: sorted.content,
        range: range,
        options: options,
        preRenderData: this.preRenderData[`${dataId}~${sorted.content.slug}`],
        source: 'invisual',
      },
      referenceID,
    );
  };

  private emitCachedRawData = (dataId, recipients, referenceID) => {
    if (!this.rawData[dataId]) return;
    // console.log('[DataManager] emitting cached data:', dataId);
    this.messageBus.emit(RESPONSE_MESSAGES.RawData, recipients, { ...this.rawData[dataId] }, referenceID);
  };

  public getDataById = async (dataId) => {
    await this.fetchDataById(dataId, false);
    return this.rawData[dataId];
  };

  private requestDataById = async (dataId, recipients, embed, referenceID) => {
    await this.fetchDataById(dataId, embed);
    this.emitCachedRawData(dataId, recipients, referenceID);
  };

  private fetchDataById = async (dataId, embed) => {
    if (!dataId || this.rawData[dataId]) return;

    let res;
    const baseDataId = dataId.split('~')[0];

    if (embed) {
      // todo, add access levels?
      try {
        res = await this.dataService.getDataByIdPromise(baseDataId);
      } catch (error) {
        console.log('[DataManager] getDataById error:', baseDataId, error);
      }
    } else {
      // get data from session store
      // TODO DATA-TABLE-HOT get from session storage here
      try {
        if (sessionStorage) {
          const sessionData = sessionStorage.getItem('clone-data');
          const _data = JSON.parse(sessionData);
          if (_data && dataId === _data.dataId) {
            this.rawData[dataId] = {
              _id: dataId,
              data: _data.data,
            };
            console.log('[DataManager] - got data from session storage:', dataId, this.rawData[dataId]);
            return;
          }

          sessionStorage.removeItem('clone-data');
        }
      } catch (error) {
        console.log('[DataManager] sessionStorage error:', error);
      }
      // get data from server
      try {
        res = await this.dataService.getDataByIdPromise(baseDataId);
      } catch (error) {
        console.log('[DataManager] getDataById error:', baseDataId, error);
      }

      if (!res) {
        if (!this.isServer()) {
          try {
            if (sessionStorage) {
              const msg = sessionStorage.getItem('data');
              if (msg) {
                const storageItem = JSON.parse(msg);
                if (dataId === storageItem._id) {
                  res = storageItem;
                } else {
                  sessionStorage.removeItem('data');
                }
              }
            }
          } catch (error) {
            console.log('[DataManager] sessionStorage error:', error);
          }
        }
      }
    }

    if (res?.nextPage) {
      let nextPage = res.nextPage;
      while (nextPage) {
        const pageData = await this.dataService.getDataByIdPromise(nextPage);
        res.data = res.data.concat(pageData.data);
        if (pageData.nextPage) {
          nextPage = pageData.nextPage;
        } else {
          nextPage = false;
        }
      }
    }

    if (res?._id && res.data) {
      this.rawData[dataId] = res;
      console.log('[DataManager]', 'setData', dataId, { data: this.rawData[dataId] });
    } else {
      // fake no data
      this.rawData[dataId] = {
        _id: dataId,
        data: [],
      };
    }
  };
}
